// SEO data + shared FAQ content for the Home page.
import { brand } from "@/config/brand";
const SITE_URL = brand.url;

export const FAQ_CATEGORIES = [
  {
    label: 'Getting Started',
    faqs: [
      { q: "I don't know anything about tech. Will this work for me?", a: "Yes — that's exactly why we exist. We handle all the technical heavy lifting so you don't have to. We explain everything in plain English, no jargon, no condescension. If you can send an email, you can work with us." },
      { q: 'Do you only work with large companies?', a: "Not at all. We work with small businesses, tradespeople, and new businesses. We build scalable systems that fit your current size and budget, with room to grow as you do." },
      { q: 'What happens in the first consultation?', a: "We have a relaxed 30-minute call to understand your business, your goals, and where you're currently stuck. There's no hard sell — just an honest conversation about whether and how we can help." },
      { q: 'Do I need to have a clear brief before contacting you?', a: "Absolutely not. Many of our best client relationships started with 'I know something needs to change but I'm not sure what.' That's what we're here to figure out together." },
    ],
  },
  {
    label: 'Websites & Ownership',
    faqs: [
      { q: 'Do I own my website and domain?', a: "100%. We set it up, but you hold the keys. No hostage situations. If you ever want to move on (though we hope you won't), you take everything with you — code, content, domain, the lot." },
      { q: 'How long does a new website take?', a: "Using our AI-assisted workflows, we can often get a stunning, high-converting site live in a matter of weeks rather than months. The exact timeline depends on complexity, but we'll give you a realistic estimate upfront." },
      { q: 'Can you work with my existing website?', a: "Yes. We can audit, improve, or rebuild your existing site depending on what makes most sense. We'll give you an honest assessment rather than pushing you towards a full rebuild if it isn't needed." },
      { q: 'Will my website work on mobile?', a: "Every site we build is mobile-first and tested across devices. With over 60% of web traffic now on mobile, this isn't optional — it's the baseline." },
    ],
  },
  {
    label: 'Pricing & Process',
    faqs: [
      { q: 'How much does it cost?', a: "It depends on the scope of work. We offer fixed-price projects and ongoing retainers. We'll always give you a clear, itemised quote before any work begins — no surprises." },
      { q: 'Do you offer payment plans?', a: "Yes. For larger projects we typically split payments into milestones — a deposit to start, a payment at a mid-point, and the final balance on delivery. We can discuss what works for your cash flow." },
      { q: 'What does the ongoing support look like?', a: "After launch, we offer monthly retainers covering updates, performance monitoring, and strategic advice. You can also opt for ad-hoc support if you prefer not to commit to a monthly plan." },
      { q: "How do I know I'm getting value for money?", a: "We set clear goals at the start of every engagement and report against them. You'll always know what we're doing, why we're doing it, and what result it's producing." },
    ],
  },
  {
    label: 'Digital Marketing & Strategy',
    faqs: [
      { q: 'Can you help me get found on Google?', a: "Yes. SEO, AEO, and GEO are built into everything we do — from site structure and page speed to content strategy and local search. We don't promise overnight rankings, but we do build sustainable, long-term visibility." },
      { q: 'Do you run social media for businesses?', a: "We can help you build a social media strategy and create content, but we focus on the channels that actually drive results for your business rather than being everywhere at once." },
      { q: "What's the difference between digital strategy and just 'doing marketing'?", a: "Most businesses jump straight into tactics — posting on Instagram, running ads — without a clear strategy. We start with your business goals and work backwards to identify which digital activities will actually move the needle." },
    ],
  },
];

// FAQPage schema built from the homepage FAQ — the structured data answer
// engines (ChatGPT, Gemini, Perplexity, Google AI Overviews) quote most.
export const homeFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_URL}/#faq`,
  mainEntity: FAQ_CATEGORIES.flatMap((cat) =>
    cat.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  ),
};

export const homeWebPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: `${SITE_URL}/`,
  name: 'Digitally Baffled LTD — Your Managed Digital Partner',
  description:
    'Digitally Baffled LTD is the one partner that manages your business\'s entire digital setup — websites, client portal & app, marketing, tools and support — consolidated and run for you. Founded by Morgan Antell, based in Ringwood, Hampshire. Websites from £500, apps from £1,500, PPC & Google Ads, SEO, AEO & GEO, ongoing support £50–£75/hr, Vision to Launch from £950. UK-wide and worldwide.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#organization` },
  datePublished: '2024-01-01',
  dateModified: '2026-06-19',
  inLanguage: 'en-GB',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
    ],
  },
};

export const homeServicesItemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Digitally Baffled Services',
  url: `${SITE_URL}/services`,
  description: 'Full list of digital transformation services offered by Digitally Baffled LTD',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'AI Web & App Design — from £500 (websites) / £1,500 (apps)', url: `${SITE_URL}/services/ai-web-app-design` },
    { '@type': 'ListItem', position: 2, name: 'Marketing (Google Ads, Facebook Ads, Instagram Ads) — priced after free assessment', url: `${SITE_URL}/services/marketing` },
    { '@type': 'ListItem', position: 3, name: 'Ongoing Support — £50–£75 per hour', url: `${SITE_URL}/services/ongoing-support` },
    { '@type': 'ListItem', position: 4, name: 'Vision to Launch — from £950', url: `${SITE_URL}/services/vision-to-launch` },
  ],
};
